import { useEffect } from "react";
import { createPortal } from "react-dom";
import { MailLogo } from "@/features/boot/MailLogo";
import {
  type MailBootStepId,
  type MailBootStepStatus,
} from "@/lib/boot/mailBootConstants";
import type { MailBootOptimization } from "@/hooks/useMailAppBoot";

export type MailBootStepView = {
  id: MailBootStepId;
  label: string;
  hint: string;
  status: MailBootStepStatus;
};

type Props = {
  label?: string;
  /** Mode chargement (barre) ou statut figé (login / erreur) */
  mode?: "loading" | "status";
  exiting?: boolean;
  steps?: MailBootStepView[];
  progress?: number;
  detail?: string | null;
  indeterminate?: boolean;
  optimization?: MailBootOptimization | null;
  onSkipBackground?: () => void;
};

export function MailBootScreen({
  label = "Démarrage…",
  mode = "loading",
  exiting = false,
  optimization,
  onSkipBackground,
}: Props) {
  const optimizeMode = optimization != null && optimization.total > 0;
  const splashLabel = optimizeMode
    ? `${optimization.pending} message${optimization.pending > 1 ? "s" : ""} en préparation`
    : label;

  useEffect(() => {
    window.unlockSuiteAppShell?.();
  }, []);

  const content = (
    <div
      className={`mail-boot mail-boot--overlay${exiting ? " mail-boot--exiting" : ""}`}
      role="status"
      aria-live="polite"
      aria-busy={mode === "loading" && !exiting}
    >
      <div className="mail-boot__glow mail-boot__glow--a" aria-hidden />
      <div className="mail-boot__glow mail-boot__glow--b" aria-hidden />
      <div className="mail-boot__mesh" aria-hidden />

      <div className="mail-boot__stage">
        <div className="mail-boot__mark">
          <span className="mail-boot__icon-wrap">
            <MailLogo className="mail-boot__logo" size={88} />
          </span>
        </div>

        <p className="mail-boot__eyebrow">Aurélia</p>
        <h1 className="mail-boot__title">Mail</h1>

        <p className="mail-boot__label">{splashLabel}</p>

        {optimizeMode || mode === "loading" ? (
          <div className="mail-boot__bar" aria-hidden>
            <span className="mail-boot__bar-fill" />
          </div>
        ) : (
          <div className="mail-boot__dots" aria-hidden>
            <span />
            <span />
            <span />
          </div>
        )}

        {optimizeMode && onSkipBackground ? (
          <button type="button" className="mail-boot__skip-btn" onClick={onSkipBackground}>
            Continuer en arrière-plan
          </button>
        ) : null}
      </div>
    </div>
  );

  if (typeof document !== "undefined") {
    return createPortal(content, document.body);
  }

  return content;
}
